import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';

const Calendar = ({ onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  const getPrice = (date) => {
    return isWeekend(date) ? 145 : 125;
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (clickedDate >= today) {
      setSelectedDate(clickedDate);
      onDateSelect(clickedDate, getPrice(clickedDate));
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-3"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = selectedDate && 
        date.toDateString() === selectedDate.toDateString();
      const isPast = date < today;
      const price = getPrice(date);
      
      days.push(
        <div
          key={day}
          onClick={() => !isPast && handleDateClick(day)}
          className={`p-1 sm:p-1.5 md:p-2 text-center border-2 transition-all duration-300 transform cursor-pointer rounded-lg min-h-[50px] sm:min-h-[55px] md:min-h-[60px] flex flex-col justify-center items-center ${
            isSelected ? 'bg-teal-600 text-white shadow-lg scale-105 -translate-y-1 border-teal-400' : 
            isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : 
            'bg-white hover:shadow-lg hover:-translate-y-1 hover:scale-105 hover:bg-teal-50 hover:border-teal-500 border-gray-200'
          }`}
        >
          <div className="font-bold text-xs sm:text-sm md:text-base leading-tight mb-0.5">{day}</div>
          <div className={`text-[9px] sm:text-[10px] md:text-xs leading-tight font-medium ${isSelected ? 'text-white' : isPast ? 'text-gray-300' : 'text-red-600'}`}>
            ${price}/h
          </div>
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl p-2 sm:p-4 md:p-5 border-2 border-gray-200 hover:shadow-3xl transition-all duration-300">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth(-1)}
          className="p-1 sm:p-1.5 hover:shadow-lg hover:-translate-y-1 hover:scale-105 transition-all duration-300 transform"
        >
          <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
        
        <div className="flex items-center gap-1 sm:gap-2">
          <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-teal-600" />
          <h2 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth(1)}
          className="p-1 sm:p-1.5 hover:shadow-lg hover:-translate-y-1 hover:scale-105 transition-all duration-300 transform"
        >
          <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 md:gap-2 mb-1 sm:mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-0.5 sm:p-1 md:p-1.5 text-center text-[10px] sm:text-xs md:text-sm font-semibold text-gray-600 min-h-[20px] sm:min-h-[25px] md:min-h-[30px] flex items-center justify-center">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 md:gap-2">
        {renderCalendar()}
      </div>

      <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row justify-center gap-1 sm:gap-4 text-[10px] sm:text-xs text-gray-700">
        <div className="flex items-center justify-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 bg-teal-600 rounded"></div>
          <span>Weekdays: $125/h</span>
        </div>
        <div className="flex items-center justify-center gap-1 sm:gap-2">
          <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-600 rounded"></div>
          <span>Weekends: $145/h</span>
        </div>
      </div>
    </div>
  );
};

export default Calendar;